import cn from 'clsx'
import { replace } from 'connected-react-router'
import moment from 'moment'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'
import styled from 'styled-components'

import { pushWithRegion, replaceWithRegion } from 'actions/NavigationActions'
import { fetchPopularHotelFilters } from 'actions/OfferActions'
import { fetchSpecificPlaces, fetchDestinationImageId } from 'actions/PlaceActions'
import { getListName } from 'analytics/enhanced-ecommerce'
import {
  searchOfferListFilters,
  searchResultDealClick,
  searchResultImpressionCount,
  searchSessionDealClick,
} from 'analytics/eventDefinitions'
import { impressionEventWithContext, productClickEventWithContext, searchEvent } from 'analytics/snowplow/events'
import { addGTMEvent } from 'api/googleTagManager'
import {
  OfferListEventHandler,
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import { SearchFormRef } from 'components/Search'
import { EnabledFilters } from 'components/Search/type'
import { getAppliedFilterCount } from 'components/Search/utils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import {
  HOTEL_SEARCH_TYPES,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_BUNDLE_AND_SAVE,
  OFFER_TYPE_VILLA,
} from 'constants/offer'
import {
  landmarkSearchSortByTypes,
  searchSortByTypes,
  SORT_OPTION_CUSTOMER_RATING
} from 'constants/offerListFilters'
import { OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import { MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON, MALDIVES_BUNDLE_DEFAULT_NIGHTS, CHARTER_PAGE_SLUG } from 'constants/charterFlight'
import {
  ANYWHERE_NAME,
  ANYWHERE_PLACE_ID,
  FLEXIBLE_DURATION_RANGE,
  LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL,
  LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL_NATURAL_LANGUAGE,
  LOCATION_SEARCH_INPUT_TITLE_LABEL,
  LOCATION_SEARCH_INPUT_TITLE_LABEL_NATURAL_LANGUAGE,
} from 'constants/search'
import {
  GLOBAL_SEARCH_INITIAL_STATE,
  GlobalSearchAction,
  GlobalSearchStateActions,
} from 'contexts/GlobalSearch/GlobalSearchState'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import {
  convertDateRangesToMonthString,
  createRecentPropertySearchObject,
  createSearchSessionKey,
  decodeSearchParams,
  deserialiseOccupancy,
  getDistanceEqFilter,
  getDistanceLteFilter,
  getSearchKey,
  getSearchTypeFromFilters,
  getsScheduleVisibilitiesFilter, isStandardOccupancy,
  mapFlexibleNightsToDurationRange,
} from 'lib/search/searchUtils'
import {
  deleteSearchParams,
  parseSearchString,
  setManySearchParamValues,
  setSearchParamValue,
  validateBoundsSearchParams,
  validateDestinationSearchParams,
  validateLandmarkSearchParams,
  validateLatLngSearchParams,
  validateNaturalLanguageSearchParams,
  validatePropertySearchParams,
} from 'lib/url/searchUrlUtils'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'

import { addRecentSearch } from 'actions/SearchActions'
import * as Analytics from 'analytics/analytics'
import {
  mapGlobalSearchContextToSnowplowSearchEvent,
  mapOfferListFiltersToFilterContexts, mapOfferListFiltersToSortContext,
} from 'analytics/mapSnowplowSearchTracking'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import LoadingAnimationBox from 'components/Common/LoadingAnimationBox/LoadingAnimationBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import Heading from 'LuxKit/Typography/Heading'
import OfferList, { OfferListV2Item } from 'components/OfferList/OfferList'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import RefreshSearchModal from 'components/SearchV2/Components/Mobile/RefreshSearchModal'
import useSlimMobileSearch from 'components/SearchV2/Hooks/useSlimMobileSearch'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { FlightsClassTypes } from 'constants/flight'
import config from 'constants/config'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import ModalContext from 'contexts/ModalContext'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import useOfferList, { TrackingOptions } from 'hooks/Offers/useOfferList'
import useOfferListExtras from 'hooks/Offers/useOfferListExtras'
import useOfferListFilters from 'hooks/Offers/useOfferListFilters'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import useOffers from 'hooks/Offers/useOffers'
import useOffersWithProperty from 'hooks/Offers/useOffersWithProperty'
import useStreamSearchExpired from 'hooks/Offers/useStreamSearchExpired'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import useSearchEvent from 'hooks/Search/useSearchEvent'
import { useCurrentSiteTakeoverV2 } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import useQueryParams from 'hooks/useQueryParams'
import { without } from 'lib/array/arrayUtils'
import getOfferListKey from 'lib/offer/offerListKey'
import { SEARCH_STREAMING_PAGE_SIZE } from 'search/initialiseSearchSocket'
import { buildSelectDatesSearchParams } from 'lib/search/selectDatesUtils'
import HotelSearchHeader from './HotelSearchHeader'
import HotelSearchNoSearchResultSection from './HotelSearchNoSearchResultSection'
import MaldivesBanner from './MaldivesBanner'
import OfferCountAndSort from './OfferCountAndSort'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import { getSource } from 'selectors/offerSelectors'
import { excludeNullOrUndefined } from 'checkout/utils'
import { CompactFooter, OriginalFooter } from 'components/App/Footer'
import HotelSearchPageFilterPane, {
  FilterControlChangeHandler,
} from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import Group from '../../utils/Group'
import { useInView } from 'react-intersection-observer'
import OfferListLoadingOfferTile from '../../OfferList/OfferListTiles/OfferListLoadingOfferTile'
import { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import useSortWasApplied from 'hooks/Search/useSortWasApplied'
import useFilterWasApplied from 'hooks/Search/useFilterWasApplied'
import useTrackingSearchEvent from 'hooks/Search/useTrackingSearchEvent'
import OfferSelectionStickyFooter from 'components/OfferComparison/OfferSelectionStickyFooter'
import uuidV4 from 'lib/string/uuidV4Utils'
import usePropertyTags from '../../../hooks/Offers/usePropertyTags'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import useSearchSession from '../../../hooks/Search/useSearchSession'
import SiteTakeoverBannerWrapper from 'takeovers/utils/components/SiteTakeoverBannerWrapper'
import HotelSearchNoSearchResultSectionAlternativeOffers
  from 'components/Pages/HotelSearchPage/HotelSearchNoSearchResultSectionAlternativeOffers'
import { getAccommodationSearchHeadMeta } from './utils/getAccommodationSearchHeadMeta'
import { usePrevious } from '@dnd-kit/utilities'

const destinationTypes = new Set(['destination'])

const ANALYTICS_HOTEL_SEARCH_TAG = 'HS'

const PageWrapper = styled(Group)`
  height: calc(var(--vh, 1vh) * 100);
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.default.eight};
    display: grid;
    padding-top: ${rem(24)};
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 2fr);
      padding-top: ${rem(12)};
    }
  }

  &.fullWidth {
    padding: ${rem(16)} 0;
  }
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
`

function mapState(state: App.State) {
  const filters = getUrlOfferListFilters(state)
  return {
    windowSearch: state.router.location.search,
    filters,
    memberId: state.auth.account.memberId,
    searchResultMetadata: state.offer.searchResultMetadata,
    isFlexibleSearch: !!(filters?.flexibleMonths && filters?.flexibleNights),
    specificPlaces: state.destination.specificPlaces[filters.destinationId ?? ''],
    region: state.geo.currentRegionCode,
  }
}

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

interface Props extends PropsFromRedux, RouteComponentProps<any> {}

function HotelSearchPage({
  filters,
  match,
  searchResultMetadata,
  windowSearch,
  isFlexibleSearch,
  location,
  history,
  specificPlaces,
}: Props) {
  const dispatch = useAppDispatch()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const currentPageHash = useAppSelector(state => state.router.location.hash)
  const isMobileScreen = useScreenSizeOnly('mobile')
  const isDesktop = useScreenSizeUp('largeDesktop')
  const searchType = getSearchTypeFromFilters(filters)
  const params = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const searchParams = useMemo(() => decodeSearchParams(windowSearch), [windowSearch])
  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const source = useAppSelector(state => getSource(state))
  const showSlimMobile = useSlimMobileSearch()
  const pageSize = SEARCH_STREAMING_PAGE_SIZE
  const destinationImageId = useAppSelector(state =>
    filters.destinationId ? state.destination.places[filters.destinationId]?.imageId : undefined,
  )

  const [selectedFilters, setSelectedFilters] = useState<App.SelectedFilters | undefined>(undefined)

  const siteTakeover = useCurrentSiteTakeoverV2({ vertical: 'hotel' })
  const enableTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const initialPage = useRef<number>(params.page ? parseInt(params.page) : 1)
  const naturalLanguageSearch = !!useOptimizelyExperiment(OptimizelyFeatureFlags.searchNaturalLanguageSearch)

  const takeoverBanner = useMemo(() => {
    if (enableTakeoverBannerContent?.enabled || siteTakeover) {
      return <SiteTakeoverBannerWrapper vertical="hotel" />
    }
    return null
  }, [enableTakeoverBannerContent, siteTakeover])

  const handleViewBundlesClick = useCallback(() => {
    // Navigate to Maldives charter page
    dispatch(pushWithRegion(`/campaign/${CHARTER_PAGE_SLUG}`))
  }, [dispatch])

  const searchKey = getSearchKey(params)
  const { searchEvent: searchSessionSearchEvent } = useSearchEvent(searchKey)

  const searchSessionKey = createSearchSessionKey({
    destinationId: params.destinationId,
    propertyId: params.propertyId,
    landmarkId: params.landmarkId,
    lat: params.lat,
    lng: params.lng,
    naturalLanguageQuery: params.naturalLanguageQuery
  })
  const { searchSessionId } = useSearchSession(searchSessionKey)

  const {
    searchTargetLabel,
    offerDistanceFromSearchTarget,
  } = useMemo(() => ({
    searchTargetLabel: params.landmarkName || params.destinationName || params.propertyName || params.naturalLanguageQuery,
    offerDistanceFromSearchTarget: searchResultMetadata.distanceFromSearchTarget[params.landmarkId || params.destinationId || ''],
  }), [
    params.landmarkId,
    params.landmarkName,
    params.destinationId,
    params.destinationName,
    params.propertyName,
    params.naturalLanguageQuery,
    searchResultMetadata.distanceFromSearchTarget,
  ])

  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const isCurrentRegionUserLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const payAtPropertyEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.croPayAtProperty)

  useEffect(() => {
    if (searchParams?.searchItem?.searchType === 'destination') {
      dispatch(fetchSpecificPlaces(searchParams.searchItem.value))
    }
  }, [dispatch, searchParams.searchItem])

  useEffect(() => {
    if (destinationImageId || !filters.destinationId || filters.destinationId === ANYWHERE_PLACE_ID) {
      return
    }

    dispatch(fetchDestinationImageId(filters.destinationId))
  }, [dispatch, filters.destinationId, destinationImageId])

  const formRef = useRef<SearchFormRef>(null)

  const onBedbankSelectDatesPrompt = useCallback((offerId: string, offerType: App.OfferType, windowSearch: string) => {
    const paramsToSet = [{ paramName: 'offerType', paramValue: offerType }, { paramName: 'offerId', paramValue: offerId }]
    if (isMobileScreen) {
      toggleSearchVertical('hotels', { withDateSelect: true, search: setManySearchParamValues(windowSearch, paramsToSet) })
    } else {
      history.replace({ search: setManySearchParamValues(windowSearch, paramsToSet) })
      formRef.current?.form.current.scrollIntoView()
      formRef.current?.openDatePickerMenu()
    }
  }, [history, isMobileScreen, toggleSearchVertical])
  const queryParams = useQueryParams()

  useIdleTimeReporter(IdleTimeSource.HotelSearchPage)

  const invalidSearchParams = useMemo(() =>
    !validateDestinationSearchParams(params) &&
    !validateLandmarkSearchParams(params) &&
    !validatePropertySearchParams(params) &&
    !validateBoundsSearchParams(params) &&
    !validateLatLngSearchParams(params) &&
    !validateNaturalLanguageSearchParams(params),
  [params])

  useEffect(() => {
    const onSearchUrlPath = match.url === location.pathname
    if (invalidSearchParams && onSearchUrlPath) {
      dispatch(replaceWithRegion('/hotels'))
    }
  }, [dispatch, invalidSearchParams, location.pathname, match])

  useEffect(() => {
    dispatch(fetchPopularHotelFilters())
  }, [dispatch])

  const whiteLabelsRanking = !!useOptimizelyExperiment(OptimizelyExperiments.searchWhiteLabelsRanking)

  const fijiPlaceId = 'le_72b32a1f754ba1c09b3695e0cb6cde7f'
  const maldivesPlaceId = 'le_2723d092b63885e0d7c260cc007e8b9d'
  const isFijiSearch = filters.destinationId === fijiPlaceId
  const isMaldivesSearch = filters.destinationId === maldivesPlaceId
  const maldivesCharterEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.croMaldiveCharter)
  const hideMarriottOffers = !!useOptimizelyExperiment(OptimizelyExperiments.searchHideMarriottOffers, isFijiSearch)

  const propertySpecificSearch = !!filters.propertyId

  const { tags: propertyTags, fetching: fetchingPropertyTags } = usePropertyTags(
    filters.propertyId,
    { disabled: !propertySpecificSearch }
  )
  const propertyNotLocationSensitive = useMemo(() =>
    !fetchingPropertyTags && !propertyTags?.includes('location sensitive'),
  [fetchingPropertyTags, propertyTags])

  const flexibleSearch = !!(isFlexibleSearch || selectedFilters?.flexibleTravelDates)
  const isAnytimeSearch = ((!filters?.checkIn && !filters?.checkOut) && !flexibleSearch) &&
    !(selectedFilters?.travelDates || selectedFilters?.flexibleTravelDates)

  const showFlashInNearbyPropertySearch = propertySpecificSearch && !isAnytimeSearch && propertyNotLocationSensitive
  const nearbyRankingInPropertySearch = !!useOptimizelyExperiment(
    OptimizelyExperiments.searchNearbyRankingInPropertySearch,
    propertySpecificSearch && !fetchingPropertyTags && propertyTags?.includes('location sensitive')
  )
  const hasStandardOccupancy = isStandardOccupancy(filters)
  const showRoomBreakdown = useOptimizelyExperiment(OptimizelyExperiments.searchShowRoomBreakdown, !propertySpecificSearch && !hasStandardOccupancy) as 'roomSplit' | 'roomSplitWithReRank' | undefined

  const rawFilters = useMemo((): App.OfferListFilters => {
    let offerTypes: Array<App.OfferListOfferType> = [...HOTEL_SEARCH_TYPES, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_BED_BANK]
    if (config.ENABLE_VILLA_RENTALS) {
      offerTypes.push(OFFER_TYPE_VILLA)
    }
    if (filters.offerTypes?.includes('rental')) {
      offerTypes = filters.offerTypes
    } else if (filters.offerTypes?.includes('hotel')) {
      offerTypes = without(offerTypes, OFFER_TYPE_VILLA)
    }

    return {
      ...filters,
      offerTypes,
      distanceEq: getDistanceEqFilter(searchType),
      distanceLte: getDistanceLteFilter(searchType),
      scheduleVisibilities: getsScheduleVisibilitiesFilter(isSpoofed),
      include: ['nearby'],
      source,
      isStream: true,
      rollout: [
        whiteLabelsRanking ? 'whiteLabelsRanking' : undefined,
        hideMarriottOffers ? 'hideMarriottOffers' : undefined,
        showFlashInNearbyPropertySearch ? 'showLosFlashInNearby' : undefined,
        nearbyRankingInPropertySearch ? 'nearbyRanking' : undefined,
        showRoomBreakdown === 'roomSplit' ? 'roomSplit' : undefined,
        showRoomBreakdown === 'roomSplitWithReRank' ? 'roomSplitWithReRank' : undefined,
        payAtPropertyEnabled ? 'payAtPropertyEnabled' : undefined,
        // always on during the AB testing, to be removed once the feature flag is no longer needed
        'useDynamicInclusionsFilter',
      ].filter(excludeNullOrUndefined),
    }
  }, [filters, searchType, isSpoofed, source, whiteLabelsRanking, hideMarriottOffers, showFlashInNearbyPropertySearch, nearbyRankingInPropertySearch, showRoomBreakdown, payAtPropertyEnabled])

  const prevNaturalLanguageQuery = usePrevious(rawFilters.naturalLanguageQuery)

  const listFilters = useMemo<App.OfferListFilters>(() => {
    // When there is a new natural language query, use the raw filters
    // as the selected filters are not set yet
    if (prevNaturalLanguageQuery !== rawFilters.naturalLanguageQuery) {
      return rawFilters
    }

    return {
      ...rawFilters,
      occupancies: selectedFilters?.occupancy
        ? deserialiseOccupancy(selectedFilters.occupancy)
        : rawFilters.rooms,
      propertyId: selectedFilters?.propertyId ?? rawFilters.propertyId,
      destinationId: selectedFilters?.placeIds?.[0] ?? rawFilters.destinationId, // nls supports multiple places however filters only support one
      flexibleNights: selectedFilters?.flexibleTravelDates?.numbersOfNights
        ? mapFlexibleNightsToDurationRange(selectedFilters.flexibleTravelDates.numbersOfNights)
        : rawFilters.flexibleNights as FLEXIBLE_DURATION_RANGE,
      flexibleMonths: selectedFilters?.flexibleTravelDates?.dateRanges
        ? convertDateRangesToMonthString(selectedFilters.flexibleTravelDates.dateRanges)
        : rawFilters.flexibleMonths,
      durationRange: rawFilters.durationRange as Array<FLEXIBLE_DURATION_RANGE>,
      checkIn: selectedFilters?.travelDates?.checkIn ?? rawFilters.checkIn,
      checkOut: selectedFilters?.travelDates?.checkOut ?? rawFilters.checkOut,
      amenities: selectedFilters?.amenities?.length ? selectedFilters.amenities : rawFilters.amenities,
      inclusions: selectedFilters?.inclusions?.length ? selectedFilters.inclusions : rawFilters.inclusions,
      rawFiltersKey: getOfferListKey(rawFilters),
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rawFilters,
    selectedFilters?.flexibleTravelDates?.dateRanges,
    selectedFilters?.flexibleTravelDates?.numbersOfNights,
    selectedFilters?.occupancy,
    selectedFilters?.placeIds,
    selectedFilters?.propertyId,
    selectedFilters?.travelDates?.checkIn,
    selectedFilters?.travelDates?.checkOut,
    selectedFilters?.amenities,
    selectedFilters?.inclusions,
  ])

  const listKey = getOfferListKey(listFilters)

  const searchId = useMemo(() => {
    return uuidV4()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey])

  const globalHotelsSearchDispatchRef = useRef<React.Dispatch<GlobalSearchAction> | null>(null)

  const onAnyHotelSelectDatesPrompt = useCallback((offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary) => {
    const { propertySearchItem, mobileSearchParams } = buildSelectDatesSearchParams(offer)

    if (globalHotelsSearchDispatchRef.current) {
      globalHotelsSearchDispatchRef.current({
        type: GlobalSearchStateActions.SET_SEARCH_ITEM,
        searchItem: propertySearchItem,
      })
    }

    if (isMobileScreen) {
      toggleSearchVertical('hotels', {
        withDateSelect: true,
        search: mobileSearchParams,
      })
    } else {
      formRef.current?.form.current.scrollIntoView()
      formRef.current?.openDatePickerMenu()
    }
  }, [isMobileScreen, toggleSearchVertical])

  const utmBp = useAppSelector(state => state.utm.bp)
  const hideAnytimePricesABTest = !!useOptimizelyExperiment(
    OptimizelyExperiments.sppHideAnytimePrices,
    utmBp !== '2' && isAnytimeSearch
  )

  const hideAnytimePricesEnabled = hideAnytimePricesABTest && isAnytimeSearch

  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    searchTargetLabel,
    offerDistanceFromSearchTarget,
    onBedbankSelectDatesPrompt,
    onAnyHotelSelectDatesPrompt,
    hideAnytimePricesEnabled,
  }, queryParams)

  useEffect(() => {
    globalHotelsSearchDispatchRef.current = globalHotelsSearchDispatch
  }, [globalHotelsSearchDispatch])

  useEffect(() => {
    globalHotelsSearchDispatch({
      type: GlobalSearchStateActions.SET_HIDE_ANYTIME_PRICES_ENABLED,
      hideAnytimePricesEnabled,
    })
  }, [hideAnytimePricesEnabled, globalHotelsSearchDispatch])

  useEffect(() => {
    globalHotelsSearchDispatch({
      type: GlobalSearchStateActions.SET_OFFER_DISTANCE_FROM_SEARCH_TARGET,
      offerDistanceFromSearchTarget,
    })
  }, [offerDistanceFromSearchTarget, globalHotelsSearchDispatch])

  useEffect(() => {
    globalHotelsSearchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_TARGET_LABEL,
      searchTargetLabel,
    })
  }, [searchTargetLabel, globalHotelsSearchDispatch])

  const overrideDestinationIds = useMemo<Array<string> | undefined>(() => {
    if (selectedFilters?.propertyId) {
      return [selectedFilters.propertyId]
    }
    if (selectedFilters?.placeIds) {
      return selectedFilters.placeIds
    }
    return undefined
  }, [selectedFilters])

  const overrideDestinationType = useMemo<'location' | 'property' | undefined>(() => {
    if (selectedFilters?.propertyId) {
      return 'property'
    }
    if (selectedFilters?.placeIds) {
      return 'location'
    }
    return undefined
  }, [selectedFilters])

  const searchContext = useMemo(() => {
    return mapGlobalSearchContextToSnowplowSearchEvent({
      ...globalHotelsSearchState,
      occupancies: selectedFilters?.occupancy
        ? deserialiseOccupancy(selectedFilters.occupancy)
        : globalHotelsSearchState.occupancies,
      flexibleNights: selectedFilters?.flexibleTravelDates?.numbersOfNights
        ? mapFlexibleNightsToDurationRange(selectedFilters.flexibleTravelDates.numbersOfNights)
        : rawFilters.flexibleNights as FLEXIBLE_DURATION_RANGE,
      flexibleMonths: selectedFilters?.flexibleTravelDates?.dateRanges
        ? convertDateRangesToMonthString(selectedFilters.flexibleTravelDates.dateRanges)
        : rawFilters.flexibleMonths,
      durationRange: rawFilters.durationRange as Array<FLEXIBLE_DURATION_RANGE>,
      checkinDate: selectedFilters?.travelDates?.checkIn ?
          moment(selectedFilters.travelDates.checkIn) :
          (rawFilters.checkIn ? moment(rawFilters.checkIn) : undefined),
      checkoutDate: selectedFilters?.travelDates?.checkOut ?
          moment(selectedFilters.travelDates.checkOut) :
          (rawFilters.checkOut ? moment(rawFilters.checkOut) : undefined),
      searchItem: searchParams?.searchItem,
      overrideDestinationIds,
      overrideDestinationType,
      eventAnalytics: { searchId },
    })
  }, [
    globalHotelsSearchState,
    rawFilters,
    searchParams?.searchItem,
    searchId,
    overrideDestinationIds,
    overrideDestinationType,
    selectedFilters?.travelDates?.checkIn,
    selectedFilters?.travelDates?.checkOut,
    selectedFilters?.flexibleTravelDates?.numbersOfNights,
    selectedFilters?.flexibleTravelDates?.dateRanges,
    selectedFilters?.occupancy,
  ])

  const filterContexts = useMemo(() => {
    return mapOfferListFiltersToFilterContexts({ offerListFilters: listFilters })
  }, [listFilters])

  const sortContext = useMemo(() => {
    return mapOfferListFiltersToSortContext({ offerListFilters: listFilters })
  }, [listFilters])

  const sortWasApplied = useSortWasApplied(listFilters)

  const filterWasApplied = useFilterWasApplied(listFilters)

  const trackingSearchEvent = useTrackingSearchEvent({
    searchSessionSearchEvent, sortWasApplied, filterWasApplied,
  })

  const offerListFilters = useOfferListFilters(listFilters)

  const minRequiredOffers = useMemo(() => {
    return (parseInt(params.pages ?? '1') * pageSize) || undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.pages])

  const trackingOptions = useMemo<TrackingOptions>(() => (
    {
      searchEvent: trackingSearchEvent,
      searchContext,
      filterContexts,
      sortContext,
      searchSessionId,
      disable: !!(listFilters.naturalLanguageQuery && !selectedFilters),
    }
  ), [trackingSearchEvent, searchContext, filterContexts, sortContext, searchSessionId, listFilters.naturalLanguageQuery, selectedFilters])

  const disableOfferList = propertySpecificSearch && fetchingPropertyTags

  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const enableVersion2Ranking = !!useOptimizelyExperiment(OptimizelyExperiments.searchAnalyticEventRanking, !canViewLuxPlusBenefits)
  const evVersion = enableVersion2Ranking ? 'version_2' : undefined

  const hotelOfferList = useOfferList(listFilters, { disabled: disableOfferList, evVersion }, trackingOptions)

  useEffect(() => {
    setSelectedFilters(hotelOfferList.selectedFilters)
  }, [hotelOfferList.selectedFilters])

  const searchListKey = hotelOfferList.key

  const recentSearchOffer = useOffersWithProperty(hotelOfferList.offerIds, searchParams.searchItem?.value)
  useEffect(() => {
    const { searchItem, dates: specificDates, rooms, flexibleSearch } = searchParams

    if (searchItem && (destinationTypes.has(searchType) || searchType === 'property' || searchType === 'landmark')) {
      const dates = {
        checkIn: specificDates?.checkIn,
        checkOut: specificDates?.checkOut,
        flexibleMonths: flexibleSearch?.flexibleMonths,
        flexibleDuration: flexibleSearch?.flexibleNights,
        isAnytime: !(specificDates?.checkIn || specificDates?.checkOut || flexibleSearch?.flexibleMonths || flexibleSearch?.flexibleNights),
      }

      const recentSearchItem = (searchType === 'property' && recentSearchOffer) ?
          createRecentPropertySearchObject(searchItem, recentSearchOffer) :
        searchItem

      dispatch(addRecentSearch(recentSearchItem, dates, rooms))
    }
  }, [dispatch, recentSearchOffer, searchParams, searchType])

  const deprecatedSearchSessionId = useAppSelector(state => state.analytics.search.sessions[searchListKey])

  const listDepth = useRef<number>(0)
  useEffect(() => {
    return () => {
      if (hotelOfferList.offerIds.length) {
        // On a new search, finalise tracking the depth reached on the old search
        addGTMEvent(searchResultImpressionCount(hotelOfferList.offerIds.length, listDepth.current))
        // event sent, now reset the depth we're at. They probably applied a filter and will be at the top again
        listDepth.current = 0
      }
    }
    // eslint-disable-next-line
  }, [searchListKey])

  useEffect(() => {
    if (listFilters) {
      addGTMEvent(searchOfferListFilters(listFilters))
    }
  }, [listFilters])

  const tracking = useOfferListTracking(
    getListName('OfferList', ANALYTICS_HOTEL_SEARCH_TAG, 'Search Page Offer List'),
    {
      snowplow: {
        enabled: false,
      },
      key: getOfferListKey(listFilters),
    },
  )

  const onPageChange = useCallback((page: number) => {
    dispatch(replace({ search: setSearchParamValue(windowSearch, 'pages', page), hash: currentPageHash }))
    // eslint-disable-next-line
  }, [windowSearch, currentPageHash])

  const sortOptions = useMemo(() => {
    const options = searchType === 'landmark' ? landmarkSearchSortByTypes : searchSortByTypes

    return [...options, SORT_OPTION_CUSTOMER_RATING]
  }, [searchType])

  const showModal = useContext(ModalContext)
  const streamedExtras = useOfferListExtras(listFilters)
  const searchExpired = useStreamSearchExpired(listFilters)

  useEffect(() => {
    if (searchExpired) {
      Analytics.trackClientEvent({
        subject: 'expired',
        action: 'show',
        category: 'search',
        type: 'nonInteraction',
      })

      showModal(<RefreshSearchModal />)
    }
  }, [searchExpired, showModal])

  const mainListFetching = hotelOfferList.fetching

  // const naturalLanguageSearchZeroSearchResult = !!params.naturalLanguageQuery &&
  //   (hotelOfferList.offerCount === 0 && hotelOfferList.offerIds?.length === 0 && !mainListFetching)

  const Footer = config.footer.type === 'original' ? OriginalFooter : CompactFooter

  const [showFooter, setShowFooter] = useState<boolean>(false)
  const [overrideOfferListFetching, setOverrideOfferListFetching] = useState<boolean>(false)
  const [numberOfInitialLoadingTilesToDisplay, setNumberOfInitialLoadingTilesToDisplay] = useState<number>(() => {
    return (params.pages ? parseInt(params.pages) : 1) * pageSize
  })
  const [maldivesBundlePrice, setMaldivesBundlePrice] = useState<number>(MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON)
  const [isMaldivesBundlePriceLoading, setIsMaldivesBundlePriceLoading] = useState<boolean>(false)

  // Calculate return date based on check-in + default nights for Maldives bundle
  const maldivesReturnDate = useMemo(() => {
    if (!listFilters.checkIn) return undefined
    return moment(listFilters.checkIn).add(MALDIVES_BUNDLE_DEFAULT_NIGHTS, 'days').format('YYYY-MM-DD')
  }, [listFilters.checkIn])

  // Get charter flight price for Maldives bundle (Melbourne-Maldives route)
  const [flightPrice, flightPriceLoading] = useCharterFlightPrice({
    charterBundleRoute: 'melbourne-maldives',
    departDate: filters.checkIn,
    returnDate: maldivesReturnDate,
    occupancies: filters.rooms ?? [],
    fareClass: FlightsClassTypes.ECONOMY,
    disabled: !isMaldivesSearch || !maldivesCharterEnabled || !filters.checkIn,
  })

  // Create a separate search for cheapest hotel (only LE hotels, sorted by price)
  // Use maldivesReturnDate (checkIn + 5 nights) for the bundle
  const cheapestHotelFilters = useMemo<App.OfferListFilters | undefined>(() => {
    if (!isMaldivesSearch || !maldivesCharterEnabled || !listFilters.checkIn || !maldivesReturnDate) {
      return undefined
    }
    return {
      ...listFilters,
      checkOut: maldivesReturnDate, // Override with bundle's return date (5 nights)
      offerTypes: ['hotel'], // Only LE hotels, not bedbank
      sortBy: 'price.asc', // Sort by price ascending
    }
  }, [isMaldivesSearch, maldivesCharterEnabled, listFilters, maldivesReturnDate])
  // Get cheapest hotel offer list (disabled when not Maldives)
  const cheapestHotelOfferList = useOfferList(cheapestHotelFilters, { disabled: !cheapestHotelFilters })

  // Get the first offer (cheapest) from the sorted list
  const [cheapestOffers] = useOffers<App.HotelOffer>(cheapestHotelOfferList.offerIds.slice(0, 1))

  // Get stable reference to cheapest offer ID to avoid unnecessary recalculations
  const cheapestOfferId = cheapestOffers[0]?.id

  // Reset price when search params change
  useEffect(() => {
    setMaldivesBundlePrice(MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON)
    setIsMaldivesBundlePriceLoading(true)
  }, [listFilters.checkIn, listFilters.rooms, params.destinationId])

  // Calculate bundle price when both flight and cheapest hotel are available
  useEffect(() => {
    if (!isMaldivesSearch || !maldivesCharterEnabled || !listFilters.checkIn || !maldivesReturnDate) {
      setIsMaldivesBundlePriceLoading(false)
      return
    }

    // Set loading state
    setIsMaldivesBundlePriceLoading(flightPriceLoading || cheapestHotelOfferList.fetching)

    // Calculate price when everything is ready
    if (!flightPriceLoading && !cheapestHotelOfferList.fetching && cheapestOfferId && listFilters.rooms) {
      const cheapestOffer = cheapestOffers[0]

      const pkg = cheapestOffer.packages.find(pkg => pkg.duration === MALDIVES_BUNDLE_DEFAULT_NIGHTS)

      if (pkg) {
        // Use memberPrice (discounted price), fall back to price
        const hotelPackagePrice = pkg.memberPrice || pkg.price

        // Calculate total travellers (adults + children across all rooms)
        const totalTravellers = listFilters.rooms.reduce((sum, room) => {
          return sum + room.adults + (room.children || 0)
        }, 0)

        // Calculate per person price
        // - flightPrice: total for all travellers
        // - hotelPackagePrice: total for all rooms
        // - perPersonPrice: (flightPrice + hotelPackagePrice) / totalTravellers
        const totalBundlePrice = flightPrice + hotelPackagePrice
        const perPersonPrice = totalTravellers > 0 ? totalBundlePrice / totalTravellers : totalBundlePrice

        // Use calculated price only if both flight and hotel prices are valid (> 0)
        if (perPersonPrice > 0 && flightPrice > 0) {
          setMaldivesBundlePrice(perPersonPrice)
        } else {
          setMaldivesBundlePrice(MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON)
        }
      } else {
        setMaldivesBundlePrice(MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON)
      }
    }
  }, [isMaldivesSearch, maldivesCharterEnabled, flightPrice, flightPriceLoading, cheapestHotelOfferList.fetching, listFilters.checkIn, maldivesReturnDate, listFilters.rooms, cheapestOfferId, cheapestOffers])

  const [headerRef, isInView] = useInView()

  const onEndReached = useCallback(() => {
    setShowFooter(true)
  }, [])

  useEffect(() => {
    setShowFooter(false)
  }, [searchKey])

  useEffect(() => {
    // when the search key changes and the main list is not fetching, we scroll the user to the top of the page
    if (mainListFetching) {
      setOverrideOfferListFetching(true)
      return
    }

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [searchKey, mainListFetching])

  useEffect(() => {
    if (!mainListFetching && isInView) {
      setOverrideOfferListFetching(false)
    }
  }, [isInView, mainListFetching])

  useEffect(() => {
    /**
     * Updates the number of loading tiles to display for the next search when:
     * 1. The current search has completed (not fetching)
     * 2. The pages parameter has changed
     *
     * Logic:
     * - Calculate tiles based on current page number * page size
     * - Cap at maximum of 10 tiles to prevent excessive loading states
     */
    if (!mainListFetching && !overrideOfferListFetching) {
      const pageNumber = params.pages ? parseInt(params.pages) : 1
      const maxNumberOfTilesToShow = 10

      setNumberOfInitialLoadingTilesToDisplay(
        Math.min(pageNumber * pageSize, maxNumberOfTilesToShow),
      )
    }
  }, [mainListFetching, overrideOfferListFetching, pageSize, params.pages])

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [dispatch])

  const mainListTotal = hotelOfferList.offerCount ?? 0
  const totalOffersForMeta = mainListTotal || hotelOfferList.offerIds?.length || 0

  const searchHeadMeta = useMemo(() => getAccommodationSearchHeadMeta({
    searchItem: searchParams.searchItem,
    destinationId: listFilters.destinationId,
    destinationName: listFilters.destinationName,
    totalCount: totalOffersForMeta,
    specificPlaces,
    destinationImageId,
  }), [
    searchParams.searchItem,
    listFilters.destinationId,
    listFilters.destinationName,
    totalOffersForMeta,
    specificPlaces,
    destinationImageId,
  ])

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    // we only want to fire this event on the main offer list
    // and not the extra offer list to avoid dup events
    if (dispatchAction.type === OfferListEvents.listLoad && searchListKey === dispatchAction.key) {
      Analytics.trackEvent(searchEvent('offer-list-load', 'hotels-search-page', {
        filters: listFilters,
        searchItem: searchParams.searchItem,
        checkIn: listFilters.checkIn,
        checkOut: listFilters.checkOut,
        vertical: 'hotels',
        occupancies: listFilters.rooms,
      }))
    }
    if (dispatchAction.type === OfferListEvents.productClick) {
      const { offer, position, price, memberPrice, duration, extendedAvailability, availability } = dispatchAction
      addGTMEvent(searchResultDealClick())
      addGTMEvent(searchSessionDealClick(deprecatedSearchSessionId, offer.id))
      Analytics.trackEvent(
        productClickEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration, searchContext },
        ),
      )
      if (extendedAvailability) {
        Analytics.trackClientEvent({
          subject: 'clicked',
          action: `${extendedAvailability}`,
          category: 'extended-window',
          type: 'interaction',
        })
      }
    } else if (dispatchAction.type === OfferListEvents.productImpression) {
      const { offer, position, availability, price, memberPrice, duration, extendedAvailability, roomRateId } = dispatchAction
      listDepth.current = Math.max(position + 1, listDepth.current)
      Analytics.trackEvent(
        impressionEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration, searchContext, roomRateId },
        ),
      )
      if (extendedAvailability) {
        Analytics.trackClientEvent({
          subject: 'viewed',
          action: `${extendedAvailability}`,
          category: 'extended-window',
          type: 'interaction',
        })
      }
    }
  }, [deprecatedSearchSessionId, tracking.listName, tracking.listId, searchContext, searchListKey, listFilters, searchParams.searchItem])

  const isZSR = (
    (
      destinationTypes.has(searchType) ||
      (searchType === 'naturalLanguage' && !selectedFilters?.propertyId)
    ) &&
    mainListTotal === 0 && !mainListFetching
  )

  const enabledFilters = useMemo((): EnabledFilters => (
    {
      inclusions: true,
      amenities: true,
      customerRating: true,
      priceFilter: config.ENABLE_HOTEL_SEARCH_PAGE_PRICE_FILTER,
      holidayTypes: searchTargetLabel !== ANYWHERE_NAME,
      locations: searchTargetLabel == ANYWHERE_NAME,
      offerTypes: config.ENABLE_VILLA_RENTALS,
      popularFilters: true,
      luxPlusFeatures: luxPlusEnabled,
      agentHubFeatures: isAgentHubEnabled,
      loyalty: isCurrentRegionUserLoyaltyRegion,
      bedrooms: true,
      beds: true,
      paymentOptions: payAtPropertyEnabled,
    }
  ), [searchTargetLabel, luxPlusEnabled, isCurrentRegionUserLoyaltyRegion, payAtPropertyEnabled])

  const filterCount = useMemo(() => {
    return getAppliedFilterCount(listFilters, enabledFilters)
  }, [listFilters, enabledFilters])

  const hasFiltersApplied = filterCount > 0

  if (invalidSearchParams) {
    return null
  }

  const positionOfFirstSeparator = streamedExtras.length > 0 ? streamedExtras[0].position : undefined

  const noExactMatchesForPropertySearch = !mainListFetching && positionOfFirstSeparator === 0

  const showNaturalLanguageSearchNoResults = (!!params.naturalLanguageQuery && isZSR) ||
    !!(selectedFilters?.propertyId && noExactMatchesForPropertySearch && mainListTotal === 0)

  const showFilterPane = !showNaturalLanguageSearchNoResults && isDesktop &&
    (mainListTotal > 0 ||
      mainListFetching ||
      hasFiltersApplied ||
      ((searchType === 'property' || listFilters?.propertyId) && !noExactMatchesForPropertySearch)
    )

  const offerListFiltersProps = {
    sortOptions,
    searchType,
    switchView: {
      label: 'Map view',
      view: 'map' as const,
      URL: mapViewURL,
    },
    listFilters: listFilters,
  }

  const sortByPrice = listFilters.sortBy === 'price.asc' || listFilters.sortBy === 'price.desc'
  const sortByValue = listFilters.sortBy === 'value'
  const showLoadingAnimation = mainListFetching && !isAnytimeSearch && (sortByPrice || sortByValue)

  const numberOfInvalidOffers = hotelOfferList.numberOfInvalidOffers
  const numberOfValidOffers = hotelOfferList.numberOfValidOffers

  const extras: Array<OfferListV2Item> = [{
    id: 'no-results',
    position: 'start',
    element: <HotelSearchNoSearchResultSection searchType={searchType} />,
    show: !(params.naturalLanguageQuery && naturalLanguageSearch) && (noExactMatchesForPropertySearch || isZSR),
  },
  {
    id: 'natural-language-search-no-results',
    position: 'start',
    element: <HotelSearchNoSearchResultSectionAlternativeOffers />,
    show: showNaturalLanguageSearchNoResults,
  },
  {
    id: 'heading',
    position: 'start',
    element: <OfferCountAndSort
      destinationName={params.destinationName}
      specificPlaces={specificPlaces}
      fetching={mainListFetching}
      mainListTotal={mainListTotal}
      sortOptions={sortOptions}
      mapViewUrl={mapViewURL}
      isFlexible={flexibleSearch}
      filters={listFilters}
      windowSearch={windowSearch}
      shouldShowFilters={!showFilterPane && !offerListFilters.fetching}
      showHolidayTypes={searchTargetLabel !== ANYWHERE_NAME}
      naturalLanguageQuery={params.naturalLanguageQuery}
      numberOfInvalidOffers={numberOfInvalidOffers}
      numberOfValidOffers={numberOfValidOffers}
      searchType={selectedFilters?.propertyId ? 'property' : searchType}
      enabledFilters={enabledFilters}
      onFilterChange={handleFilterChange}
      onSortChange={handleSortChange}
    />,
    show: (!isZSR),
  },
  {
    id: 'loading-animation',
    position: 'start',
    element: <LoadingAnimationBox variant="hotels" />,
    show: showLoadingAnimation,
  },
  {
    id: 'scrolling-loading',
    position: 'start',
    element: <>
      {Array.from({ length: numberOfInitialLoadingTilesToDisplay || 4 }).map((_, index) => (
        <OfferListLoadingOfferTile
          key={`loading-tile-${index}`}
          tileStyle="search"
          offerType="hotel"
          className="offer-tile"
          filters={listFilters}
          offerPosition={index}
        />
      ))}
    </>,
    show: overrideOfferListFetching ? 'always' : false,
  },
  ...streamedExtras.map(extra => ({
    id: extra.id,
    position: extra.position,
    element: <Heading variant="heading4">{extra.heading}</Heading>,
    show: 'hasOffers' as const,
  })),
  {
    id: 'maldives-banner',
    position: 0,
    element: <MaldivesBanner
      onViewBundlesClick={handleViewBundlesClick}
      price={maldivesBundlePrice}
      isPriceLoading={isMaldivesBundlePriceLoading}
    />,
    show: isMaldivesSearch && maldivesCharterEnabled,
  },
  ]

  return <PageWrapper direction="vertical" verticalAlign="space-between" gap={16}>
    <div>
      <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
        <GlobalSearchContextProvider
          state={globalHotelsSearchState}
          dispatch={globalHotelsSearchDispatch}
        >
          <HeadData
            noIndex
            title={searchHeadMeta.title}
            description={searchHeadMeta.description}
            imageId={searchHeadMeta.imageId}
            imageUrl={searchHeadMeta.imageUrl}
            imageAlt={searchHeadMeta.imageAlt}
          />
          {takeoverBanner}
          <div ref={headerRef} />
          <HotelSearchHeader
            filters={listFilters}
            formRef={formRef}
            offerListFiltersProps={offerListFiltersProps}
            searchVertical="hotels"
            baseSearchPath="/search"
            baseSearchMapPath="/search/map"
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
            locationSearchPlaceholder={naturalLanguageSearch ? LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL_NATURAL_LANGUAGE : LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL}
            label={naturalLanguageSearch ? LOCATION_SEARCH_INPUT_TITLE_LABEL_NATURAL_LANGUAGE : LOCATION_SEARCH_INPUT_TITLE_LABEL}
            showNaturalLanguageSearchSuggestions={naturalLanguageSearch}
          />
          <ErrorBoundary fallback={null}>
            <PromptCardSelector tripReengagement />
          </ErrorBoundary>
          <StyledLayoutContainer
            size={showFilterPane ? 'xl' : 'lg'}
            className={cn({ filterPane: showFilterPane, fullWidth: showSlimMobile })}
          >
            <StyledVerticalSpacer gap={16}>
              <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
                <OfferList
                  filters={listFilters}
                  interstitials={extras}
                  onPageChange={onPageChange}
                  tileStyle="search"
                  eagerLoadFirstImage
                  defaultPage={initialPage.current}
                  fullWidth={showSlimMobile}
                  pageSize={pageSize}
                  clientCheckAvailability
                  searchId={searchId}
                  showLoadingTiles={!showLoadingAnimation}
                  onEndReached={onEndReached}
                  overrideFetching={overrideOfferListFetching}
                  minRequiredOffers={minRequiredOffers}
                  disableUseOfferList={disableOfferList}
                  evVersion={evVersion}
                />
              </OfferListEventsProvider>
            </StyledVerticalSpacer>
            {showFilterPane && <HotelSearchPageFilterPane
              filters={listFilters}
              mapViewURL={mapViewURL}
              search={windowSearch}
              enabledFilters={enabledFilters}
              hotelOfferList={hotelOfferList}
              mainListFetching={mainListFetching}
              doNotScrollToTopOfSelection
              showLoadingSkeletonOnEachFilterSelection
              bedroomsFilterGroupAtBottom={isStandardOccupancy(listFilters)}
              onFilterChange={handleFilterChange}
            />}
          </StyledLayoutContainer>

          <OfferSelectionStickyFooter />
        </GlobalSearchContextProvider>
      </AnalyticsPageContext.Provider>
    </div>
    {showFooter && <Footer />}
  </PageWrapper>
}

export default withRouter(connector(HotelSearchPage))
